import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronRight, Home, Users, Target, BookOpen, Star, Award, TrendingUp, BarChart3, Calculator, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import image1 from "./assets/earth-3537401_1280.jpg";
import missionImg from "./assets/ian-schneider-TamMbr4okv4-unsplash.jpg";
import visionImg from "./assets/fractal-1746832_1280.jpg";
import ourStoryImg from "./assets/moon-6374498_1280.jpg";
import teanImg from "./assets/puzzle-7507357_1280.jpg";
import approachImg from "./assets/finance-8836903_1280.jpg";
import whyUseImag from "./assets/question-mark-1872665_1280.jpg";

// Enhanced HTMLFlipBook with proper swipe support
const HTMLFlipBook = ({ children, width, height, size, minWidth, maxWidth, minHeight, maxHeight, maxShadowOpacity, flippingTime, showCover, mobileScrollSupport, className, ...props }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const totalPages = React.Children.count(children);

  // Minimum swipe distance (in px) to trigger page change
  const minSwipeDistance = 50;

  const nextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
  }, [totalPages]);

  const prevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevPage();
      } else if (e.key === 'ArrowRight') {
        nextPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextPage, prevPage]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextPage(); // Swipe left goes to next page
    } else if (isRightSwipe) {
      prevPage(); // Swipe right goes to previous page
    }

    setIsDragging(false);
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handlePageClick = (e) => {
    // Don't handle click if we were dragging
    if (isDragging) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const pageWidth = rect.width;

    // If clicked on left half, go to previous page
    // If clicked on right half, go to next page
    if (clickX < pageWidth / 2) {
      prevPage();
    } else {
      nextPage();
    }
  };
  
  return (
    <div
      className={className}
      style={{ width, height }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative">
        {React.Children.map(children, (child, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 cursor-pointer ${
              index === currentPage ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            onClick={handlePageClick}
          >
            {child}
          </div>
        ))}
        
        {/* Visual click zones (optional - for user feedback) */}
        <div className="absolute inset-0 pointer-events-none z-20">
          <div className="absolute left-0 top-0 w-1/2 h-full flex items-center justify-start pl-4 opacity-0 hover:opacity-20 transition-opacity bg-gradient-to-r from-blue-500 to-transparent">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <span className="text-blue-500 text-sm">←</span>
            </div>
          </div>
          <div className="absolute right-0 top-0 w-1/2 h-full flex items-center justify-end pr-4 opacity-0 hover:opacity-20 transition-opacity bg-gradient-to-l from-purple-500 to-transparent">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <span className="text-purple-500 text-sm">→</span>
            </div>
          </div>
        </div>
        
        {/* Navigation dots */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-30">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentPage(i);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentPage ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Enhanced PageCover Component
const PageCover = React.forwardRef(({ children, isEnd = false }, ref) => {
  return (
    <div className="page page-cover" ref={ref} data-density="hard">
      <div className="page-content flex flex-col items-center justify-center h-full relative">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-purple-900/80 to-indigo-900/80 rounded-xl"></div>
        
        {/* Quantitative Finance Floating Elements */}
        <div className="absolute top-8 left-8 w-12 h-12 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-lg flex items-center justify-center animate-bounce">
          <TrendingUp className="w-6 h-6 text-green-300" />
        </div>
        <div className="absolute top-16 right-12 w-10 h-10 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full flex items-center justify-center animate-bounce delay-500">
          <BarChart3 className="w-5 h-5 text-blue-300" />
        </div>
        <div className="absolute bottom-24 left-12 w-8 h-8 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full flex items-center justify-center animate-bounce delay-1000">
          <Calculator className="w-4 h-4 text-purple-300" />
        </div>
        <div className="absolute bottom-32 right-8 w-14 h-14 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-lg flex items-center justify-center animate-bounce delay-1500">
          <Zap className="w-6 h-6 text-yellow-300" />
        </div>

        {/* Mathematical Symbols Background */}
        <div className="absolute inset-0 opacity-10 text-white/20 font-mono text-xs">
          <div className="absolute top-20 left-20">∑</div>
          <div className="absolute top-32 right-24">∫</div>
          <div className="absolute bottom-40 left-16">π</div>
          <div className="absolute bottom-28 right-20">√</div>
          <div className="absolute top-40 left-32">∂</div>
          <div className="absolute bottom-40 right-32">Δ</div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center p-8">
          {!isEnd ? (
            <>
              {/* Enhanced Icon Section */}
              <div className="mb-8">
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-2xl transform hover:scale-105 transition-transform duration-300">
                    <TrendingUp className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-3 h-3 text-white" />
                  </div>
                </div>
              </div>

              {/* Enhanced Title */}
              <div className="mb-6">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  Welcome to
                </h1>
                <h2 className="text-3xl md:text-4xl font-extrabold text-transparent bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300 bg-clip-text tracking-wide">
                  KSQuantitative
                </h2>
              </div>

              {/* Enhanced Subtitle */}
              <div className="mb-8 max-w-md">
                <p className="text-lg text-blue-100 mb-2 font-medium">
                  Elite Quantitative Finance
                </p>
                <p className="text-base text-blue-200/90 leading-relaxed">
                  Algorithmic Trading • Risk Management • Financial Engineering
                </p>
              </div>

              {/* Professional Navigation Hint */}
              <div className="flex items-center gap-3 text-white/70 text-sm bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm border border-white/20">
                <BarChart3 className="w-4 h-4" />
                <span>Explore our expertise</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center mb-4 mx-auto shadow-xl">
                  <Award className="w-8 h-8 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-6">The End</h2>
              <Link
                to="/service"
                className="group bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-6 py-3 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-300 font-semibold"
              >
                <span className="flex items-center gap-2">
                  Check Our Services
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

// Enhanced Page Component  
const Page = React.forwardRef(({ number, imageSrc, header, children, icon: Icon }, ref) => {
  const contentRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [children]);

  // Removed touch handlers to prevent interference with main flipbook navigation
  // Pages can still be scrolled normally with touch, but won't interfere with left/right swipes

  return (
    <div className="page" ref={ref}>
      <div
        className="page-content overflow-y-auto bg-white rounded-lg shadow-xl"
        ref={contentRef}
        style={{
          height: '100%',
          overscrollBehavior: 'contain'
        }}
      >
        {/* Enhanced Header Section */}
        <div className="relative bg-gradient-to-br from-blue-600 to-purple-700 p-4 text-white">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              {Icon && (
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Icon className="w-5 h-5 text-white" />
                </div>
              )}
              <h2 className="text-xl font-bold">{header}</h2>
            </div>
            {imageSrc && (
              <div className="w-full h-20 bg-white/10 rounded-lg overflow-hidden backdrop-blur-sm">
                <img
                  src={imageSrc}
                  alt={header}
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Enhanced Content Section */}
        <div className="p-4">
          <div className={`transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}>
            <div className="text-gray-700 text-sm leading-relaxed">
              {typeof children === 'string' ? (
                <p>{children}</p>
              ) : (
                children
              )}
            </div>
          </div>
        </div>
        
        {/* Enhanced Page Footer */}
        <div className="page-footer absolute bottom-4 right-4">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-white text-xs font-semibold">{number + 1}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

const AboutUs = () => {
  const flipbookRef = useRef(null);

  return (
    <section
      className="vanta-bg-container relative min-h-screen w-full text-white flex justify-center"
      style={{
        color: "whitesmoke",
        paddingTop: "80px", // Account for navbar height
        alignItems: "center",
      }}
    >
      <HTMLFlipBook
        width={350}
        height={500}
        size="fixed"
        minWidth={250}
        maxWidth={600}
        minHeight={300}
        maxHeight={800}
        maxShadowOpacity={0.05}
        flippingTime={2000}
        showCover={true}
        mobileScrollSupport={true}
        className="demo-book"
        ref={flipbookRef}
      >
        <PageCover />
        
        <Page number={1} imageSrc={image1} header="About Us" icon={Home}>
          Welcome to <strong>KSquantitative</strong>, a premier hub for mastering
          quantitative finance and algorithmic trading. We blend professional
          expertise with cutting-edge technology to deliver transformative
          educational experiences and robust trading solutions. Whether you're
          an investor seeking consistent returns, a trader looking to sharpen
          your skills, or a financial institution aiming to stay ahead of market
          trends, KSquantitative is here to support your financial success.
        </Page>
        
        <Page number={2} imageSrc={missionImg} header="Our Mission" icon={Target}>
          At <strong>KSquantitative</strong>, our mission is to empower investors, traders, and
          financial institutions with the knowledge, tools, and strategies
          needed to excel in the world of quantitative finance and algorithmic
          trading. We are dedicated to fostering a community of professionals
          who leverage data-driven approaches to achieve consistent, superior
          financial outcomes.
        </Page>
        
        <Page number={3} header="Our Vision" imageSrc={visionImg} icon={Star}>
          We envision a future where <strong>KSquantitative</strong> is recognized as the
          leading authority in quantitative finance and algorithmic trading
          across Africa and beyond. Our goal is to create a hub of excellence
          where innovation, research, and education converge to shape the next
          generation of financial experts.
        </Page>
        
        <Page number={4} header="Our Story" imageSrc={ourStoryImg} icon={BookOpen}>
          Founded in <strong>Lagos, Nigeria</strong>, KSquantitative was born out of a passion
          for transforming the financial landscape through quantitative methods.
          Recognizing the challenges faced by traders and investors in
          navigating complex markets, we set out to create a platform that
          bridges the gap between traditional finance and cutting-edge
          technology. With a commitment to professionalism and innovation, we've
          grown into a trusted partner for those seeking to enhance their
          financial acumen and achieve consistent trading success.
        </Page>
        
        <Page number={5} header="Our Team" imageSrc={teanImg} icon={Users}>
          Our team comprises seasoned experts in quantitative finance,
          algorithmic trading, and financial education. With years of experience
          in both the academic and practical aspects of finance, our
          professionals are dedicated to providing top-tier training and
          developing sophisticated trading algorithms. We are a collective of
          researchers, traders, and educators who are passionate about driving
          progress in the financial industry.
        </Page>
        
        <Page number={6} header="Our Approach" imageSrc={approachImg} icon={Award}>
          At <strong>KSquantitative</strong>, we believe in the power of data-driven
          decision-making. Our approach integrates advanced scientific
          techniques with practical trading strategies, ensuring that our
          clients and students are equipped with the best tools to succeed in
          today's dynamic markets. We focus on excellence, innovation, and
          continuous learning, setting our clients up for long-term success.
        </Page>
        
        <Page number={7} header="Why Choose Us?" imageSrc={whyUseImag}>
          <div className="space-y-3">
            <div>
              <h3 className="font-bold text-blue-800 mb-1">🎯 Expertise</h3>
              <p className="text-xs">Our team is composed of professionals with deep
              knowledge and experience in quantitative finance and algorithmic
              trading.</p>
            </div>
            <div>
              <h3 className="font-bold text-purple-800 mb-1">🚀 Innovation</h3>
              <p className="text-xs">We stay ahead of market trends by constantly
              refining our strategies and tools to deliver cutting-edge solutions.</p>
            </div>
            <div>
              <h3 className="font-bold text-green-800 mb-1">🤝 Community</h3>
              <p className="text-xs">We are more than just a service provider; we are a
              community committed to helping each other succeed.</p>
            </div>
          </div>
        </Page>
        
        <PageCover isEnd={true} />
      </HTMLFlipBook>
    </section>
  );
};

export default AboutUs;